import React from 'react'

import { Button, ButtonProps } from '../../../components/button/button.js'
import { Card } from '../../../components/card/card.js'
import { CheckCard } from '../../../components/forms/check-card/check-card.js'
import { Checkbox, CheckboxProps } from '../../../components/forms/checkbox/checkbox.js'
import { ChoiceGroup, ChoiceGroupProps } from '../../../components/forms/choice-group/choice-group.js'
import { ChooseMore, ChooseMoreProps } from '../../../components/forms/choose-more/choose-more.js'
import { Datepicker } from '../../../components/forms/datepicker/datepicker.js'
import { Select, SelectProps } from '../../../components/forms/select/select.js'
import { Textarea } from '../../../components/forms/textarea/textarea.js'
import { GridColumn, GridColumnProps } from '../../../components/grid/grid-column.js'
import { Grid } from '../../../components/grid/grid.js'
import { useCurrentBreakPoint } from '../../../components/helper/hooks/use-current-breakpoint.js'
import { Modal, ModalCloser, ModalContent, ModalHeader, ModalProvider } from '../../../components/modal/modal.js'
import { NotificationCard, NotificationCardProps } from '../../../components/notification-card/notification-card.js'
import { PageTitle, PageTitleProps } from '../../../components/page-title/page-title.js'
import { SubscriptionCard, SubscriptionCardProps } from '../../../components/subscription-card/subscription-card.js'
import { ClaimDamageAdditionalProps } from '../swap/swap.js'

export enum UpsellOptionType {
    SWAP = 'upsell-swap',
    CANCEL = 'upsell-cancel',
    UPGRADE = 'upsell-upgrade',
}

export interface UpsellProps extends ChoiceGroupProps {
    title: string
    confirmButton: ButtonProps
    closeButton: ButtonProps
}

export interface ClaimDamageProps {
    title: string
    damageSelect: SelectProps
    additional?: ClaimDamageAdditionalProps
}

export interface ViewCancelProps {
    title: PageTitleProps
    formTitle: string
    submitButton: ButtonProps
    subscription: SubscriptionCardProps
    disclaimer: JSX.Element
    upsell?: UpsellProps
    actionChoice?: ChoiceGroupProps
    conditionChoice?: ChooseMoreProps
    conditionTerms?: NotificationCardProps
    damageProps?: ClaimDamageProps
    notifications?: Array<{ title: string; content: string }>
    termsCheckbox?: CheckboxProps
    isBolttech?: boolean
}

export const ViewCancel = (props: ViewCancelProps): JSX.Element => {
    const bp = useCurrentBreakPoint()
    const isMobile = bp ? ['xs', 'sm'].includes(bp) : true

    const renderSubscriptionCard = (gridProps: Partial<GridColumnProps>) => (
        <GridColumn {...gridProps}>
            <Grid>
                <GridColumn>
                    <SubscriptionCard {...props.subscription} />
                </GridColumn>
                {props.conditionTerms && !isMobile && (
                    <GridColumn>
                        <NotificationCard {...props.conditionTerms} />
                    </GridColumn>
                )}
            </Grid>
        </GridColumn>
    )

    const renderUpsellModal = (upsellProps: UpsellProps): JSX.Element => (
        <Modal size="small">
            <Grid>
                <GridColumn>
                    <ModalHeader>
                        <h2>{upsellProps.title}</h2>
                    </ModalHeader>
                    <ModalContent>
                        <div className="text">
                            <Card hasContent={true} padding="medium" variant="quinary" className="bold">
                                <ChoiceGroup {...upsellProps} />
                            </Card>
                            <Grid>
                                <GridColumn width={['xs-12', 'sm-6']}>
                                    <ModalCloser>
                                        <Button {...upsellProps.closeButton} variant="secondary" isBlock />
                                    </ModalCloser>
                                </GridColumn>
                                <GridColumn width={['xs-12', 'sm-6']}>
                                    <ModalCloser>
                                        <Button {...upsellProps.confirmButton} isBlock />
                                    </ModalCloser>
                                </GridColumn>
                            </Grid>
                        </div>
                    </ModalContent>
                </GridColumn>
            </Grid>
        </Modal>
    )

    return (
        <Grid>
            <ModalProvider>
                <GridColumn>
                    <PageTitle {...props.title} alwaysButton={true} />
                </GridColumn>
                {!isMobile && renderSubscriptionCard({ width: ['md-6', 'xl-4'] })}
                <GridColumn width={['md-6', 'xl-8']}>
                    <Grid>
                        <GridColumn>
                            <Card hasContent={true} padding="large">
                                {props.disclaimer}
                            </Card>
                        </GridColumn>
                        {isMobile && renderSubscriptionCard({})}
                        <GridColumn>
                            <h3 className={isMobile ? 'text-large bold' : 'h2'}>{props.formTitle}</h3>
                        </GridColumn>

                        {props.actionChoice && (
                            <GridColumn>
                                <Card hasContent={true} padding="medium" variant="quinary">
                                    <ChoiceGroup {...props.actionChoice} />
                                </Card>
                            </GridColumn>
                        )}

                        {props.conditionChoice && (
                            <GridColumn>
                                <Card hasContent={true} padding="medium" variant="quinary">
                                    <ChooseMore {...props.conditionChoice} />
                                </Card>
                            </GridColumn>
                        )}

                        {props.upsell && renderUpsellModal(props.upsell)}

                        {props.conditionTerms && isMobile && (
                            <GridColumn>
                                <NotificationCard {...props.conditionTerms} />
                            </GridColumn>
                        )}

                        {props.damageProps && (
                            <GridColumn>
                                <Card hasContent={true} padding="large" variant="quinary">
                                    <Grid>
                                        <GridColumn>
                                            <h3 className={isMobile ? 'text-large bold' : 'h3'}>
                                                {props.damageProps.title}
                                            </h3>
                                        </GridColumn>
                                        <GridColumn>
                                            <Select {...props.damageProps.damageSelect} bordered={true} />
                                        </GridColumn>
                                        {props.damageProps.additional && !props.isBolttech && (
                                            <>
                                                {props.damageProps.additional.title && (
                                                    <GridColumn>
                                                        <p className="text-small">
                                                            {props.damageProps.additional.title}
                                                        </p>
                                                    </GridColumn>
                                                )}
                                                {props.damageProps.additional.datepicker && (
                                                    <GridColumn>
                                                        <Datepicker
                                                            {...props.damageProps.additional.datepicker}
                                                            bordered={true}
                                                        />
                                                    </GridColumn>
                                                )}
                                                {props.damageProps.additional.textarea && (
                                                    <GridColumn>
                                                        <Textarea
                                                            {...props.damageProps.additional.textarea}
                                                            bordered={true}
                                                            hiddenLabel={true}
                                                        />
                                                    </GridColumn>
                                                )}
                                                {props.damageProps.additional.damageTitle && (
                                                    <GridColumn>
                                                        <h4 className={isMobile ? 'text-small bold' : 'h3'}>
                                                            {props.damageProps.additional.damageTitle}
                                                        </h4>
                                                    </GridColumn>
                                                )}
                                                {props.damageProps.additional.specificDamage && (
                                                    <GridColumn>
                                                        <Grid>
                                                            {props.damageProps.additional.specificDamage.map(
                                                                (checkCard, index) => (
                                                                    <GridColumn width={['xs-12']} key={index}>
                                                                        <CheckCard {...checkCard} />
                                                                    </GridColumn>
                                                                ),
                                                            )}
                                                        </Grid>
                                                    </GridColumn>
                                                )}
                                            </>
                                        )}
                                    </Grid>
                                </Card>
                            </GridColumn>
                        )}

                        {props.notifications?.map((notification, index) => (
                            <GridColumn key={index}>
                                <NotificationCard {...notification} />
                            </GridColumn>
                        ))}

                        {props.termsCheckbox && props.submitButton && (
                            <>
                                <GridColumn>
                                    <Checkbox {...props.termsCheckbox} />
                                </GridColumn>

                                <GridColumn>
                                    <Button {...props.submitButton} isBlock="xs" />
                                </GridColumn>
                            </>
                        )}
                    </Grid>
                </GridColumn>
            </ModalProvider>
        </Grid>
    )
}
